import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PieChart, Pie, Cell } from 'recharts';
import { Award, Target, FileText, BarChart3 } from 'lucide-react';
import { CustomTooltip } from '@/lib/dashboard-utils.jsx';

const TIER_COLORS = ['#00ff41', '#00d1ff', '#ff0050', '#FFED00', '#E1306C', '#1877F2'];

export const QuizAnalyticsPanel = ({ quizAnalytics, quizFilters, setQuizFilters, showAllTeams, setShowAllTeams, availableTeams, availableTiers }) => (
  <div className="mb-8" data-testid="quiz-analytics-section">
    <h2 className="text-2xl font-semibold text-[#00d1ff] mb-6 border-b border-[#00d1ff]/30 pb-3">QUIZ & QUESTIONNAIRE ANALYTICS</h2>
    <div className="flex flex-wrap gap-3 mb-6" data-testid="quiz-filters">
      <FilterSelect value={quizFilters.dateRange} onChange={v => setQuizFilters(prev => ({ ...prev, dateRange: v }))} testId="quiz-filter-date"
        options={[['all', 'All Time'], ['7d', 'Last 7 Days'], ['30d', 'Last 30 Days'], ['90d', 'Last 90 Days']]} />
      <FilterSelect value={quizFilters.sport} onChange={v => setQuizFilters(prev => ({ ...prev, sport: v }))} testId="quiz-filter-sport"
        options={[['all', 'All Sports'], ['football', 'Football'], ['basketball', 'Basketball'], ['hockey', 'Hockey'], ['soccer', 'Soccer/MLS'], ['volleyball', 'Volleyball'], ['city', 'City Trivia']]} />
      <FilterSelect value={quizFilters.country} onChange={v => setQuizFilters(prev => ({ ...prev, country: v }))} testId="quiz-filter-country"
        options={[['all', 'All Countries'], ['Argentina', 'Argentina'], ['Belgium', 'Belgium'], ['Brazil', 'Brazil'], ['Canada', 'Canada'], ['England', 'England'], ['France', 'France'], ['Germany', 'Germany'], ['Italy', 'Italy'], ['Netherlands', 'Netherlands'], ['Spain', 'Spain'], ['Turkey', 'Turkey'], ['USA', 'USA']]} />
      <FilterSelect value={quizFilters.team} onChange={v => setQuizFilters(prev => ({ ...prev, team: v }))} testId="quiz-filter-team"
        options={[['all', 'All Teams'], ...(availableTeams || []).map(t => [String(t.id), t.name])]} />
      <FilterSelect value={quizFilters.tier} onChange={v => setQuizFilters(prev => ({ ...prev, tier: v }))} testId="quiz-filter-tier"
        options={[['all', 'All Tiers'], ...(availableTiers || []).map(t => [t, t])]} />
      {(quizFilters.dateRange !== 'all' || quizFilters.sport !== 'all' || quizFilters.country !== 'all' || quizFilters.team !== 'all' || quizFilters.tier !== 'all') && (
        <button
          onClick={() => setQuizFilters({ dateRange: 'all', sport: 'all', country: 'all', team: 'all', tier: 'all' })}
          className="text-xs text-[#ff0033] border border-[#ff0033]/30 px-3 py-2 rounded-lg hover:bg-[#ff0033]/10 transition-colors"
          data-testid="quiz-filter-reset"
        >
          Clear Filters
        </button>
      )}
    </div>
    {!quizAnalytics.hasData ? (
      <div className="chart-container p-12 rounded-lg text-center">
        <Award className="w-16 h-16 text-gray-600 mx-auto mb-4 opacity-30" />
        <p className="text-xl text-gray-400 mb-2">No quiz or questionnaire data yet</p>
        <p className="text-sm text-gray-500">Analytics will appear here once users complete quizzes and questionnaires</p>
      </div>
    ) : (
      <>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <KpiCard icon={<Award className="w-6 h-6 text-[#00ff41]" />} label="Quiz Completions" value={quizAnalytics.kpis.quizCompletions} color="text-[#00ff41]" testId="quiz-kpi-completions" />
          <KpiCard icon={<Target className="w-6 h-6 text-[#00d1ff]" />} label="Avg Quiz Score" value={quizAnalytics.kpis.quizAccuracy} color="text-[#00d1ff]" testId="quiz-kpi-accuracy" />
          <KpiCard icon={<FileText className="w-6 h-6 text-[#00ff41]" />} label="Questionnaire Completions" value={quizAnalytics.kpis.questionnaireCompletions} color="text-[#00ff41]" testId="quiz-kpi-quest-completions" />
          <KpiCard icon={<BarChart3 className="w-6 h-6 text-[#00d1ff]" />} label="Most Popular Tier" value={quizAnalytics.kpis.mostPopularTier} color="text-[#00d1ff] capitalize" testId="quiz-kpi-popular-tier" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="chart-container p-6 rounded-lg" data-testid="quiz-by-team-chart">
            <h3 className="text-xl font-semibold text-[#00d1ff] mb-4">QUIZ COMPLETIONS BY TEAM</h3>
            {quizAnalytics.quizByTeam.length > 0 ? (
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={quizAnalytics.quizByTeam.slice(0, 10)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" />
                  <XAxis dataKey="team_name" stroke="#666" tick={{ fill: '#999', fontSize: 11 }} angle={-45} textAnchor="end" height={80} />
                  <YAxis stroke="#666" tick={{ fill: '#999' }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" fill="#00ff41" name="Completions" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : <div className="flex items-center justify-center h-[300px] text-gray-500"><p>No quiz data by team</p></div>}
          </div>
          <div className="chart-container p-6 rounded-lg" data-testid="tier-breakdown-chart">
            <h3 className="text-xl font-semibold text-[#00d1ff] mb-4">TIER BREAKDOWN</h3>
            {quizAnalytics.tierBreakdown.length > 0 ? (
              <div className="flex items-center gap-8">
                <div className="w-1/2">
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie data={quizAnalytics.tierBreakdown} cx="50%" cy="50%" innerRadius={60} outerRadius={100} dataKey="count" nameKey="name" stroke="none">
                        {quizAnalytics.tierBreakdown.map((entry, index) => (<Cell key={entry.name} fill={TIER_COLORS[index % 6]} />))}
                      </Pie>
                      <Tooltip contentStyle={{ backgroundColor: '#000', border: '1px solid #333' }} labelStyle={{ color: '#00d1ff' }} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="w-1/2 space-y-3">
                  {quizAnalytics.tierBreakdown.map((tier, index) => (
                    <div key={tier.name} className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: TIER_COLORS[index % 6] }} />
                      <span className="text-sm text-white flex-1 capitalize">{tier.name}</span>
                      <span className="text-sm font-bold text-white">{tier.count}</span>
                      <span className="text-xs text-gray-500">{tier.percentage}%</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : <div className="flex items-center justify-center h-[250px] text-gray-500"><p>No tier data available</p></div>}
          </div>
        </div>
        {quizAnalytics.weeklyActivity.length > 0 && (
          <div className="chart-container p-6 rounded-lg mb-6" data-testid="quiz-weekly-chart">
            <h3 className="text-xl font-semibold text-[#00d1ff] mb-4">WEEKLY GAMIFICATION ACTIVITY</h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={quizAnalytics.weeklyActivity}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" />
                <XAxis dataKey="week" stroke="#666" tick={{ fill: '#999' }} />
                <YAxis stroke="#666" tick={{ fill: '#999' }} />
                <Tooltip content={<CustomTooltip />} />
                <Legend wrapperStyle={{ color: '#999' }} />
                <Line type="monotone" dataKey="quizzes" stroke="#00ff41" strokeWidth={2} dot={{ fill: '#00ff41', r: 3 }} name="Quizzes" />
                <Line type="monotone" dataKey="questionnaires" stroke="#00d1ff" strokeWidth={2} dot={{ fill: '#00d1ff', r: 3 }} name="Questionnaires" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
        {quizAnalytics.teamEngagementTable.length > 0 && (
          <div className="chart-container p-6 rounded-lg" data-testid="quiz-team-table">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-[#00d1ff]">TEAM GAMIFICATION ENGAGEMENT</h3>
              {quizAnalytics.teamEngagementTable.length > 10 && (
                <button onClick={() => setShowAllTeams(!showAllTeams)} className="text-sm text-[#00d1ff] border border-[#00d1ff]/30 px-3 py-1 rounded hover:bg-[#00d1ff]/10 transition-colors" data-testid="quiz-show-all-teams">
                  {showAllTeams ? 'Show Top 10' : `Show All (${quizAnalytics.teamEngagementTable.length})`}
                </button>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-3 px-4 text-xs text-gray-400 uppercase tracking-wide">Team</th>
                    <th className="text-center py-3 px-4 text-xs text-gray-400 uppercase tracking-wide">Quizzes</th>
                    <th className="text-center py-3 px-4 text-xs text-gray-400 uppercase tracking-wide">Questionnaires</th>
                    <th className="text-center py-3 px-4 text-xs text-gray-400 uppercase tracking-wide">Total</th>
                    <th className="text-center py-3 px-4 text-xs text-gray-400 uppercase tracking-wide">Avg Score</th>
                  </tr>
                </thead>
                <tbody>
                  {(showAllTeams ? quizAnalytics.teamEngagementTable : quizAnalytics.teamEngagementTable.slice(0, 10)).map((team) => (
                    <tr key={team.team_id} className="border-b border-gray-800 hover:bg-white/5 transition-colors">
                      <td className="py-4 px-4">
                        <div className="flex items-center gap-2">
                          <div className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: team.color || '#00d1ff', border: (team.color === '#FFFFFF' || team.color === '#000000') ? '1px solid #666' : 'none' }} />
                          <span className="text-white font-medium">{team.team_name}</span>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-center text-[#00ff41] font-semibold">{team.quizzes}</td>
                      <td className="py-4 px-4 text-center text-[#00d1ff] font-semibold">{team.questionnaires}</td>
                      <td className="py-4 px-4 text-center text-white font-bold">{team.total}</td>
                      <td className="py-4 px-4 text-center">
                        <span className={`font-semibold ${team.avgScore !== 'N/A' && Number(team.avgScore) >= 7 ? 'text-[#00ff41]' : team.avgScore !== 'N/A' && Number(team.avgScore) < 5 ? 'text-[#ff0033]' : 'text-gray-400'}`}>{team.avgScore}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </>
    )}
  </div>
);

const KpiCard = ({ icon, label, value, color, testId }) => (
  <div className="kpi-card p-4 rounded-lg" data-testid={testId}>
    <div className="flex items-center gap-2 mb-2">
      {icon}
      <span className="text-xs text-gray-400 uppercase tracking-wide">{label}</span>
    </div>
    <p className={`text-2xl font-bold ${color}`}>{value}</p>
  </div>
);

const FilterSelect = ({ value, onChange, testId, options }) => (
  <select value={value} onChange={e => onChange(e.target.value)} className="bg-black/50 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:border-[#00d1ff] focus:outline-none" data-testid={testId}>
    {options.map(([val, label]) => <option key={val} value={val}>{label}</option>)}
  </select>
);
